import { initializeApp } from 'firebase/app';
import { getDatabase, ref, set, get, child, serverTimestamp } from 'firebase/database';
import FootballService from '../../../dashboards/services/footballServices';

const RTDB_URL = 'https://just-rhythm-445002-s8-e92d0-513eb.firebaseio.com/';

const firebaseConfig = {
  databaseURL: RTDB_URL
};

let rtdbApp;
let rtdb;

try {
  rtdbApp = initializeApp(firebaseConfig, 'football-settlement');
  rtdb = getDatabase(rtdbApp);
} catch (error) {
  console.error('Failed to initialize settlement RTDB:', error);
}

class FootballSettlementService {
  constructor() {
    this.completedStatuses = ['FT', 'AET', 'PEN'];
    this.liveStatuses = ['1H', '2H', 'HT', 'ET', 'BT', 'P', 'SUSP', 'INT', 'LIVE'];
  }

  determineResult(homeScore, awayScore) {
    if (homeScore > awayScore) return 'W';
    if (homeScore < awayScore) return 'L';
    return 'D';
  }

  formatDateKey(date) {
    if (date instanceof Date) {
      // Convert to NYC Eastern Time
      const nycDate = new Date(date.toLocaleString("en-US", {timeZone: "America/New_York"}));
      const year = nycDate.getFullYear();
      const month = String(nycDate.getMonth() + 1).padStart(2, '0');
      const day = String(nycDate.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    return date;
  }

  getCurrentDateET() {
    // Get current date in NYC Eastern Time
    const nycNow = new Date(new Date().toLocaleString("en-US", {timeZone: "America/New_York"}));
    return this.formatDateKey(nycNow);
  }

  extractFixtureData(fixture) {
    return {
      gameId: fixture.fixture.id,
      date: this.formatDateKey(fixture.fixture.date),
      timestamp: fixture.fixture.timestamp,
      league: {
        id: fixture.league.id,
        name: fixture.league.name,
        country: fixture.league.country,
        season: fixture.league.season,
        round: fixture.league.round
      },
      teams: {
        home: {
          id: fixture.teams.home.id,
          name: fixture.teams.home.name
        },
        away: {
          id: fixture.teams.away.id,
          name: fixture.teams.away.name
        }
      },
      score: {
        home: fixture.goals.home,
        away: fixture.goals.away,
        halftime: {
          home: fixture.score.halftime.home,
          away: fixture.score.halftime.away
        },
        fulltime: {
          home: fixture.score.fulltime.home,
          away: fixture.score.fulltime.away
        },
        extratime: {
          home: fixture.score.extratime?.home || null,
          away: fixture.score.extratime?.away || null
        },
        penalty: {
          home: fixture.score.penalty?.home || null,
          away: fixture.score.penalty?.away || null
        }
      },
      status: {
        short: fixture.fixture.status.short,
        long: fixture.fixture.status.long,
        elapsed: fixture.fixture.status.elapsed
      }
    };
  }

  async fetchAllFixturesForDate(date) {
    const dateStr = this.formatDateKey(date);
    console.log(`📅 Fetching ALL fixtures for ${dateStr}`);

    try {
      // Fetch all fixtures for the date without league filtering
      // Using the base service with the current year as season
      const currentYear = new Date().getFullYear();
      const response = await FootballService.getFixtures(
        null,  // id
        null,  // ids
        null,  // live
        dateStr,  // date
        null,  // league (null to get all)
        currentYear,  // season
        null,  // team
        null,  // last
        null,  // next
        null,  // from
        null,  // to
        null,  // round
        null,  // status
        null,  // venue
        'UTC'  // timezone
      );

      const fixtures = response?.response || [];
      console.log(`📊 Total fixtures fetched: ${fixtures.length}`);

      // Log league distribution for monitoring
      const leagueCount = {};
      fixtures.forEach(f => {
        const leagueKey = `${f.league.country} - ${f.league.name}`;
        leagueCount[leagueKey] = (leagueCount[leagueKey] || 0) + 1;
      });

      console.log('📈 League distribution:', Object.keys(leagueCount).length, 'leagues');

      return fixtures;
    } catch (error) {
      console.error('❌ Error fetching fixtures:', error);
      throw error;
    }
  }

  async pullMorningFixtures(date = new Date()) {
    const dateKey = this.formatDateKey(date);
    console.log(`🌅 Starting morning pull for ${dateKey} (ET)`);

    try {
      const fixtures = await this.fetchAllFixturesForDate(date);
      const settlementData = {};
      let scheduledCount = 0;
      let liveCount = 0;
      let completedCount = 0;

      for (const fixture of fixtures) {
        const data = this.extractFixtureData(fixture);
        data.pullType = 'morning';
        data.settlementTime = serverTimestamp();

        if (this.liveStatuses.includes(fixture.fixture.status.short)) {
          liveCount++;
          data.result = 'LIVE';
        } else if (fixture.fixture.status.short === 'NS' || fixture.fixture.status.short === 'TBD') {
          scheduledCount++;
          data.result = 'SCHEDULED';
        } else if (this.completedStatuses.includes(fixture.fixture.status.short)) {
          completedCount++;
          const homeGoals = fixture.goals.home || 0;
          const awayGoals = fixture.goals.away || 0;
          data.result = this.determineResult(homeGoals, awayGoals);
        }

        settlementData[fixture.fixture.id] = data;
      }

      // Store in RTDB - using manual-settlements path to avoid conflicts with Cloud Functions
      const settlementRef = ref(rtdb, `manual-settlements/football/${dateKey}`);
      await set(settlementRef, settlementData);

      console.log(`✅ Morning pull complete: ${fixtures.length} fixtures`);
      console.log(`   📋 Scheduled: ${scheduledCount}, Live: ${liveCount}, Completed: ${completedCount}`);

      return {
        success: true,
        date: dateKey,
        fixtureCount: fixtures.length,
        scheduledCount,
        liveCount,
        completedCount,
        data: settlementData
      };
    } catch (error) {
      console.error('❌ Morning pull failed:', error);
      throw error;
    }
  }

  async pullNightlySettlements(date = new Date()) {
    const dateKey = this.formatDateKey(date);
    console.log(`🌙 Starting nightly settlement pull for ${dateKey} (ET)`);

    try {
      const fixtures = await this.fetchAllFixturesForDate(date);
      const updates = {};
      let settledCount = 0;
      let stillLiveCount = 0;
      let postponedCount = 0;

      for (const fixture of fixtures) {
        const statusShort = fixture.fixture.status.short;

        if (this.completedStatuses.includes(statusShort)) {
          const data = this.extractFixtureData(fixture);
          const homeGoals = fixture.goals.home || 0;
          const awayGoals = fixture.goals.away || 0;

          data.result = this.determineResult(homeGoals, awayGoals);
          data.pullType = 'nightly';
          data.settlementTime = serverTimestamp();
          data.isSettled = true;

          updates[fixture.fixture.id] = data;
          settledCount++;
        } else if (this.liveStatuses.includes(statusShort)) {
          stillLiveCount++;
          // Update live games with current score
          const data = this.extractFixtureData(fixture);
          data.result = 'LIVE';
          data.pullType = 'nightly';
          data.settlementTime = serverTimestamp();
          data.isSettled = false;

          updates[fixture.fixture.id] = data;
        } else if (['PST', 'CANC', 'ABD'].includes(statusShort)) {
          const data = this.extractFixtureData(fixture);
          data.result = 'VOID';
          data.pullType = 'nightly';
          data.settlementTime = serverTimestamp();
          data.isSettled = false;

          updates[fixture.fixture.id] = data;
          postponedCount++;
        }
      }

      // Merge with existing data - using manual-settlements path to avoid conflicts with Cloud Functions
      const dbRef = ref(rtdb, `manual-settlements/football/${dateKey}`);
      const existingSnapshot = await get(dbRef);
      const existingData = existingSnapshot.val() || {};

      const mergedData = { ...existingData, ...updates };
      await set(dbRef, mergedData);

      console.log(`✅ Nightly settlement complete:`);
      console.log(`   ✅ Settled: ${settledCount}`);
      console.log(`   ⏳ Still live: ${stillLiveCount}`);
      console.log(`   ❌ Void/Postponed: ${postponedCount}`);

      return {
        success: true,
        date: dateKey,
        settledCount,
        stillLiveCount,
        postponedCount,
        data: updates
      };
    } catch (error) {
      console.error('❌ Nightly settlement failed:', error);
      throw error;
    }
  }

  async getSettlementsByDate(date) {
    const dateKey = this.formatDateKey(date);
    const dbRef = ref(rtdb);

    try {
      const snapshot = await get(child(dbRef, `manual-settlements/football/${dateKey}`));
      if (snapshot.exists()) {
        return snapshot.val();
      }
      return null;
    } catch (error) {
      console.error('Error fetching settlements:', error);
      throw error;
    }
  }

  async getGameResult(gameId, date) {
    const dateKey = this.formatDateKey(date);
    const dbRef = ref(rtdb);

    try {
      const snapshot = await get(child(dbRef, `manual-settlements/football/${dateKey}/${gameId}`));
      if (snapshot.exists()) {
        return snapshot.val();
      }
      return null;
    } catch (error) {
      console.error(`Error fetching game ${gameId}:`, error);
      throw error;
    }
  }

  async getWinLossRecordForDate(date) {
    const settlements = await this.getSettlementsByDate(date);

    if (!settlements) {
      return { wins: 0, losses: 0, draws: 0, void: 0, scheduled: 0, live: 0, total: 0 };
    }

    const stats = {
      wins: 0,
      losses: 0,
      draws: 0,
      void: 0,
      scheduled: 0,
      live: 0,
      total: 0
    };

    Object.values(settlements).forEach(game => {
      if (game.result === 'W') stats.wins++;
      else if (game.result === 'L') stats.losses++;
      else if (game.result === 'D') stats.draws++;
      else if (game.result === 'VOID') stats.void++;
      else if (game.result === 'SCHEDULED') stats.scheduled++;
      else if (game.result === 'LIVE') stats.live++;
      stats.total++;
    });

    return stats;
  }

  async getLeagueStatsForDate(date) {
    const settlements = await this.getSettlementsByDate(date);

    if (!settlements) {
      return {};
    }

    const leagueStats = {};

    Object.values(settlements).forEach(game => {
      const leagueId = game.league.id;
      if (!leagueStats[leagueId]) {
        leagueStats[leagueId] = {
          name: game.league.name,
          country: game.league.country,
          games: 0,
          settled: 0,
          scheduled: 0,
          live: 0,
          homeWins: 0,
          awayWins: 0,
          draws: 0,
          totalGoals: 0
        };
      }

      const stats = leagueStats[leagueId];
      stats.games++;

      if (game.result === 'W') {
        stats.homeWins++;
        stats.settled++;
      } else if (game.result === 'L') {
        stats.awayWins++;
        stats.settled++;
      } else if (game.result === 'D') {
        stats.draws++;
        stats.settled++;
      } else if (game.result === 'SCHEDULED') {
        stats.scheduled++;
      } else if (game.result === 'LIVE') {
        stats.live++;
      }

      if (game.score && game.score.home !== null && game.score.away !== null) {
        stats.totalGoals += (game.score.home + game.score.away);
      }
    });

    return leagueStats;
  }

  async runManualPull(type = 'morning', date = new Date()) {
    console.log(`🔧 Running manual ${type} pull for ${this.formatDateKey(date)} (ET)`);

    if (type === 'morning') {
      return await this.pullMorningFixtures(date);
    } else if (type === 'nightly') {
      return await this.pullNightlySettlements(date);
    } else {
      throw new Error(`Invalid pull type: ${type}. Use 'morning' or 'nightly'`);
    }
  }

  async pullTodayET(type = 'morning') {
    // Always pull for "today" in NYC Eastern Time
    const nycNow = new Date(new Date().toLocaleString("en-US", {timeZone: "America/New_York"}));
    console.log(`📅 Pulling for today in ET: ${this.formatDateKey(nycNow)}`);
    return await this.runManualPull(type, nycNow);
  }

  async pullYesterdayET(type = 'nightly') {
    // Pull for yesterday in NYC Eastern Time (useful for settling completed games)
    const nycNow = new Date(new Date().toLocaleString("en-US", {timeZone: "America/New_York"}));
    nycNow.setDate(nycNow.getDate() - 1);
    console.log(`📅 Pulling for yesterday in ET: ${this.formatDateKey(nycNow)}`);
    return await this.runManualPull(type, nycNow);
  }

  async getSettlementStats(startDate, endDate) {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const allStats = {
      totalGames: 0,
      totalSettled: 0,
      homeWinPercentage: 0,
      drawPercentage: 0,
      awayWinPercentage: 0,
      byLeague: {},
      byDate: {}
    };

    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const dateKey = this.formatDateKey(d);
      const dayStats = await this.getWinLossRecordForDate(d);
      const leagueStats = await this.getLeagueStatsForDate(d);

      allStats.byDate[dateKey] = dayStats;

      Object.entries(leagueStats).forEach(([leagueId, stats]) => {
        if (!allStats.byLeague[leagueId]) {
          allStats.byLeague[leagueId] = {
            name: stats.name,
            country: stats.country,
            totalGames: 0,
            totalSettled: 0,
            homeWins: 0,
            awayWins: 0,
            draws: 0
          };
        }

        const league = allStats.byLeague[leagueId];
        league.totalGames += stats.games;
        league.totalSettled += stats.settled;
        league.homeWins += stats.homeWins;
        league.awayWins += stats.awayWins;
        league.draws += stats.draws;
      });

      allStats.totalGames += dayStats.total;
      allStats.totalSettled += (dayStats.wins + dayStats.losses + dayStats.draws);
    }

    // Calculate percentages
    if (allStats.totalSettled > 0) {
      const totalWins = Object.values(allStats.byDate).reduce((sum, d) => sum + d.wins, 0);
      const totalLosses = Object.values(allStats.byDate).reduce((sum, d) => sum + d.losses, 0);
      const totalDraws = Object.values(allStats.byDate).reduce((sum, d) => sum + d.draws, 0);

      allStats.homeWinPercentage = (totalWins / allStats.totalSettled * 100).toFixed(2);
      allStats.awayWinPercentage = (totalLosses / allStats.totalSettled * 100).toFixed(2);
      allStats.drawPercentage = (totalDraws / allStats.totalSettled * 100).toFixed(2);
    }

    return allStats;
  }
}

const settlementService = new FootballSettlementService();

// Make it available globally for debugging - ONLY IN DEVELOPMENT
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  window.footballSettlement = settlementService;
  console.log('💡 Football Settlement Service available as window.footballSettlement');
  console.log('Try: window.footballSettlement.runManualPull("morning")');
  console.log('Or: window.footballSettlement.getWinLossRecordForDate(new Date())');
}

export default settlementService;